import React from "react";
import { Link } from "react-router-dom";
import { Award, ShieldCheck, Globe2, Users, Building, MapPin, Phone, Mail, HeartHandshake, Scale, Quote, ArrowRight, Sofa, Banknote } from "lucide-react";

const STATS = [
  { icon: ShieldCheck, title: "RERA Verified", body: "Every project is RERA-checked and contractually verified." },
  { icon: Globe2, title: "NRI Specialists", body: "Dedicated team helping NRIs in 60+ countries invest in Indian realty." },
  { icon: Users, title: "3,000+ Clients", body: "A track record of repeat business and word-of-mouth referrals." },
  { icon: Award, title: "Since 2015", body: "A decade of trust, transparent dealings and long-term relationships." },
];

const SERVICES = [
  {
    icon: Banknote,
    title: "Home Loan Consultation",
    body: "Tied up with 24+ banks and HFCs — SBI, HDFC, ICICI, Axis, Bajaj, LIC HF and every nationalised bank. We negotiate the best rates and arrange sanctions in 72 hours.",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200",
    href: "/home-loans",
    cta: "Explore home loans",
  },
  {
    icon: Sofa,
    title: "Interior Works",
    body: "Turnkey interior design and execution by our in-house studio in Lucknow and partner ateliers across Gurugram, Noida, Delhi and Pune. From concept to handover in 45 days.",
    image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200",
    href: "/interior-works",
    cta: "View packages",
  },
  {
    icon: Globe2,
    title: "NRI Services",
    body: "Dedicated NRI desk for FEMA-compliant advisory, PoA documentation, repatriation, golden-visa documentation and end-to-end property management for absent owners.",
    image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1200",
    href: "/nri-services",
    cta: "NRI desk",
  },
];

const SUPPORT_SERVICES = [
  {
    icon: Building,
    title: "Property Management",
    body: "Tenant sourcing, rent collection, society dues, repairs, annual audits — your asset on autopilot, with quarterly photo reports straight to your inbox.",
  },
  {
    icon: Scale,
    title: "Legal & Registration",
    body: "Title search, encumbrance certificate, RERA verification, registration coordination — handled by our in-house legal panel before you commit a rupee.",
  },
  {
    icon: HeartHandshake,
    title: "Financial Advisory",
    body: "Portfolio structuring, tax optimisation, repatriation planning and after-sales CRM for the long ownership cycle. We're partners — not transaction-counters.",
  },
];

const TESTIMONIALS = [
  {
    name: "Dr. Gunjan Bhatnagar",
    role: "Gynaecologist & Infertility Specialist",
    quote: "I cannot speak highly enough about the professionalism and integrity of the OC Infraventures team. They genuinely cared about my satisfaction and went the extra mile to ensure that I found the perfect home.",
  },
  {
    name: "Dr. Farooqi",
    role: "Radiologist · Saudi Arabia",
    quote: "Experience with OC Infraventures was an absolute pleasure. Their professionalism, expertise and personalised approach stood out from the start. They patiently answered every question and helped me make informed decisions.",
  },
  {
    name: "Sanjeev Singh",
    role: "CIO, Farm Bureau · United States",
    quote: "Team OC truly took the time to listen, understand what I was looking for, and used their expertise to find the right fit. Their attention to detail and willingness to go above and beyond made the entire process seamless.",
  },
  {
    name: "Sandeep Shekhar",
    role: "United States",
    quote: "A pleasure to partner with OC Infraventures. True professionals who take care of every need with the lowest risk possible — single point of communication for everything. Highly recommend.",
  },
  {
    name: "Tariq Ahmed Khan",
    role: "Engineer, HAL",
    quote: "Buying a property comes with pressure — but Team OC released all of it. Special thanks to Mr Ayush Srivastava for finding the right property without pain or risks.",
  },
  {
    name: "Raj Mani Verma",
    role: "Commissioner",
    quote: "If you are looking for a real estate company that combines expertise, personalised attention and a genuine passion for helping clients find their dream homes, I wholeheartedly recommend OC Infraventures.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pb-32">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-24 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="overline mb-4">About OC Infraventures</div>
          <h1 className="font-serif text-5xl sm:text-6xl font-light text-slate-900 leading-tight">
            A leading <em className="text-[#D4A437] not-italic">real estate</em> consultancy since 2015.
          </h1>
          <div
            data-testid="about-rera-badge"
            className="mt-8 inline-flex items-start gap-3 border border-amber-200 bg-[#D4A437]/5 px-5 py-4"
          >
            <ShieldCheck className="w-5 h-5 text-[#D4A437] mt-0.5 shrink-0" strokeWidth={1.5} />
            <div>
              <div className="overline text-[#D4A437] mb-1">UP RERA Registered Agent</div>
              <div className="font-mono text-base text-slate-900 tracking-wider">UPRERAAGT15084</div>
              <div className="text-xs text-slate-600 mt-2">Verified by Uttar Pradesh Real Estate Regulatory Authority</div>
            </div>
          </div>
        </div>
        <div className="md:col-span-7 text-slate-600 leading-relaxed text-lg font-light pt-4">
          OC Infraventures &amp; Constructions Pvt Ltd, founded in 2015, is a leading real estate company
          serving thousands of clients and generating millions of dollars in transactions across residential,
          commercial and industrial real estate.
          <p className="mt-6">
            We pride ourselves on an unwavering commitment to excellence and customer satisfaction.
            Our team of experienced professionals brings deep knowledge and expertise — enabling us to
            offer comprehensive, personalised services for first-time homebuyers, seasoned investors and
            businesses looking for commercial spaces alike.
          </p>
          <p className="mt-6">
            With a diverse portfolio across Lucknow, Gurugram, Noida, Pune, Delhi, Mumbai, Bangalore,
            Hyderabad and overseas in Dubai and Singapore, we provide end-to-end solutions — buying,
            selling, renting, leasing and full-stack property management — so you have one point of
            accountability through your entire ownership cycle.
          </p>
        </div>
      </section>

      {/* Vision pillars */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24 grid md:grid-cols-4 gap-px bg-slate-100">
        {STATS.map((s) => (
          <div key={s.title} className="bg-white p-10" data-testid={`about-stat-${s.title.replace(/\s+/g, "-").toLowerCase()}`}>
            <s.icon className="w-6 h-6 text-[#D4A437] mb-5" strokeWidth={1.5} />
            <div className="font-serif text-xl text-slate-900 mb-3">{s.title}</div>
            <div className="text-sm text-slate-600 font-light leading-relaxed">{s.body}</div>
          </div>
        ))}
      </section>

      {/* Services — alternating image / text rows */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32" data-testid="about-services">
        <div className="overline mb-4">What we cover</div>
        <h2 className="font-serif text-4xl sm:text-5xl font-light text-slate-900 mb-4">A full-stack realty partner.</h2>
        <p className="text-slate-600 mb-16 max-w-2xl font-light">
          From the first site visit to the last interior touch — six pillars under one roof,
          one accountable team, one signed contract.
        </p>

        <div className="space-y-px bg-slate-100">
          {SERVICES.map((s, i) => (
            <div
              key={s.title}
              data-testid={`about-service-${s.title.replace(/\s+/g, "-").toLowerCase()}`}
              className={`grid md:grid-cols-2 gap-px bg-slate-100 ${i % 2 === 1 ? "md:[&>:first-child]:order-2" : ""}`}
            >
              <div className="relative aspect-[4/3] md:aspect-auto bg-slate-50 overflow-hidden group min-h-[280px]">
                <img
                  src={s.image}
                  alt={s.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/40 to-transparent" />
                <div className="absolute top-6 left-6 bg-white/80 backdrop-blur-sm border border-slate-200 p-3">
                  <s.icon className="w-5 h-5 text-[#D4A437]" strokeWidth={1.5} />
                </div>
              </div>
              <div className="bg-white p-10 md:p-14 flex flex-col justify-center">
                <div className="overline mb-3 text-[#D4A437]">Service · 0{i + 1}</div>
                <h3 className="font-serif text-3xl text-slate-900 mb-5">{s.title}</h3>
                <p className="text-slate-600 leading-relaxed font-light">{s.body}</p>
                {s.href && (
                  <Link
                    to={s.href}
                    data-testid={`about-service-link-${s.title.replace(/\s+/g, "-").toLowerCase()}`}
                    className="mt-8 inline-flex items-center gap-2 text-[#D4A437] hover:text-[#B88C2E] text-sm tracking-[0.15em] uppercase"
                  >
                    {s.cta} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Supporting services */}
        <div className="mt-12 grid md:grid-cols-3 gap-px bg-slate-100 border border-slate-100">
          {SUPPORT_SERVICES.map((s) => (
            <div key={s.title} className="bg-white p-8" data-testid={`about-tile-${s.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <s.icon className="w-6 h-6 text-[#D4A437] mb-5" strokeWidth={1.5} />
              <h3 className="font-serif text-lg text-slate-900 mb-3">{s.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed font-light">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Track record copy */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="overline mb-4">Why clients return</div>
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-slate-900 leading-tight">
            Reputation built on professionalism, integrity and transparent dealings.
          </h2>
        </div>
        <div className="md:col-span-7 text-slate-600 leading-relaxed text-lg font-light pt-4">
          Our success is reflected in our track record of serving over 3,000 satisfied clients across
          India and the diaspora. Long-term relationships, not one-off transactions, are what we measure.
          <p className="mt-6">
            As a company we are constantly evolving — leveraging the latest technology and market insights
            to stay ahead of the curve. Our team undergoes regular training to ensure we are well-equipped
            to meet the evolving needs of our clients and provide the highest level of service.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32" data-testid="testimonials-section">
        <div className="overline mb-4">Testimonials · Client voices</div>
        <h2 className="font-serif text-3xl sm:text-4xl font-light text-slate-900 mb-12">Trusted by professionals across continents.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-100">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="bg-white p-8" data-testid={`testimonial-${i}`}>
              <Quote className="w-6 h-6 text-[#D4A437] mb-5" strokeWidth={1.5} />
              <p className="text-sm text-slate-700 leading-relaxed font-light">{t.quote}</p>
              <div className="mt-6 pt-6 border-t border-slate-100">
                <div className="font-serif text-base text-slate-900">{t.name}</div>
                <div className="text-xs text-slate-500 mt-1">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Office */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32">
        <div className="overline mb-4">Our office</div>
        <h2 className="font-serif text-3xl sm:text-4xl font-light text-slate-900 mb-12">Visit us in Lucknow</h2>
        <div className="grid md:grid-cols-2 gap-px bg-slate-100 border border-slate-100">
          <div className="bg-white p-10">
            <div className="overline mb-3 flex items-center gap-2"><Building className="w-3.5 h-3.5" /> Headquarters</div>
            <div className="font-serif text-2xl text-slate-900 mb-4">Felix Square HQ</div>
            <div className="text-sm text-slate-600 leading-relaxed font-light flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-0.5 text-[#D4A437] shrink-0" />
              <span>504B Felix Square,<br/>International Business Bay,<br/>Sushant Golf City,<br/>Lucknow, UP 226030</span>
            </div>
            <div className="text-sm text-slate-700 mt-4 flex items-center gap-3">
              <Phone className="w-4 h-4 text-[#D4A437]" /> +91 522 4000 800
            </div>
            <div className="text-sm text-slate-700 mt-2 flex items-center gap-3">
              <Mail className="w-4 h-4 text-[#D4A437]" /> info@ocinfraventures.com
            </div>
          </div>
          <div className="bg-white p-2">
            <iframe
              title="OC Infraventures Office Location"
              data-testid="about-office-map"
              src="https://www.openstreetmap.org/export/embed.html?bbox=80.96%2C26.75%2C80.99%2C26.78&amp;layer=mapnik&amp;marker=26.7659%2C80.9762"
              className="w-full h-full min-h-[320px] border-0"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
